import { retryWithBackoff } from 'utils/retryWithBackoff';
import {
  captureCandidateContext,
  type CandidateContext,
} from 'candidate-app/candidateCapability';
import { isAxiosError } from 'axios';
import type {
  CandidateWebCallPayload,
  CheckWebLinkResponse,
} from 'api/generated/types';
import { getCall } from 'api/generated/api/call';

// Per-attempt timeouts. A healthy check_web_link responds in ~1s; without these,
// a hung connection (e.g. response lost at the proxy/network layer) stalls a
// single attempt for 100+ seconds and burns the whole retry budget on one dead
// socket. First attempts use a shorter timeout so transient blips retry faster;
// later attempts allow more time before giving up.
const EARLY_ATTEMPT_TIMEOUT_MS = 10_000;
const LATE_ATTEMPT_TIMEOUT_MS = 15_000;

export const CHECK_WEB_LINK_ATTEMPT_TIMEOUTS_MS = [
  EARLY_ATTEMPT_TIMEOUT_MS,
  EARLY_ATTEMPT_TIMEOUT_MS,
  LATE_ATTEMPT_TIMEOUT_MS,
  LATE_ATTEMPT_TIMEOUT_MS,
  LATE_ATTEMPT_TIMEOUT_MS,
  LATE_ATTEMPT_TIMEOUT_MS,
] as const;

function getCheckWebLinkAttemptTimeoutMs(attemptNumber: number): number {
  return (
    CHECK_WEB_LINK_ATTEMPT_TIMEOUTS_MS[attemptNumber - 1] ??
    LATE_ATTEMPT_TIMEOUT_MS
  );
}

function shouldRetryCheckWebLink(err: unknown): boolean {
  if (!isAxiosError(err)) return false;
  // Explicitly do not retry canceled requests.
  if (err.code === 'ERR_CANCELED') return false;

  // Network error (no HTTP response) — common during transient connectivity/cold starts.
  if (!err.response) return true;

  // Retry only on server errors.
  return err.response.status >= 500;
}

export async function checkWebLinkWithRetry(
  payload: CandidateWebCallPayload,
  opts?: {
    signal?: AbortSignal;
    candidateContext?: CandidateContext | null;
    maxAttempts?: number;
    baseDelayMs?: number;
  }
): Promise<CheckWebLinkResponse> {
  const context = opts?.candidateContext ?? captureCandidateContext();
  const maxAttempts = opts?.maxAttempts ?? 6; // 6 total attempts = 5 retries
  const baseDelayMs = opts?.baseDelayMs ?? 250;

  const { callCheckWebLink } = getCall();

  return retryWithBackoff(
    (attempt) =>
      callCheckWebLink(payload, {
        signal: opts?.signal,
        candidateContext: context,
        timeout: getCheckWebLinkAttemptTimeoutMs(attempt),
      }),
    {
      maxAttempts,
      baseDelayMs,
      // Preserve the link-check schedule: 250ms, 750ms, 1750ms, ... before jitter.
      delayMs: (attempt) => baseDelayMs * (2 ** attempt - 1),
      jitterRatio: 0.15,
      signal: opts?.signal,
      shouldRetry: shouldRetryCheckWebLink,
    }
  );
}
